import React, { useCallback, useEffect, useRef, useState } from "react";

import { Chip } from "@mui/material";
import { PickTabTemplate } from "js/jsx/src/classes/quote/pickTabTemplate";
import { useDrag, useDrop } from "react-dnd";
import { useEditorContext } from "../context/editorContext";
import { getRows, removeZeroQuantityItems } from "../Helpers";
import useDarkMode from "../WidgetToolbar/widgetToolbarHelpers";
import useDraggableExpandableHelper from "./draggableExpandableHelper";
import { useAppQuoteContext } from "../../../../../../context/AppQuoteContext";

const useDraggableExpandableContainer = (
	id,
	tabItem,
	onMove,
	open,
	duplicatedTabId,
	updateOpenTab,
	setDuplicatedTabId,
	setChange,
	setLoading
) => {
	const { contentGrid, selectedTabs, setSelectedTabs } = useEditorContext();
	const {quote} = useAppQuoteContext();
	const [disabledAddNewTab, setDisabledAddNewTab] = useState(false);
	const [gridRows, setGridRows] = useState([]);
	const [ids, setIds] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);
	const [sheetAnchorEl, setSheetAnchorEl] = useState(null);
	const [renameTab, setRenameTab] = useState(false);
	const [isSpreadsheetRefreshing, setIsSpreadsheetRefreshing] = useState(false);
	const [tabName, setTabName] = useState((tabItem && tabItem.IdQuoteTabs) ? tabItem.TabName || "" : "Add New Tab");

		const {
			getOption,
			getTabNumber,
			getTabName,
			saveTabName,
			getOptionGroupLabelColor,
			getTabGroupLabelColor,
			getOptionGroupLabelTitle,
			getTabGroupLabelTitle,
			handleDuplicateTab,
		} = useDraggableExpandableHelper(
			tabItem,
			setTabName,
			setChange,
			tabName,
			updateOpenTab,
			setDuplicatedTabId
		);
		const selectedTabIds = selectedTabs.map((tab) => tab.tabId);
		const isTabSelected = selectedTabIds.includes(tabItem?.IdQuoteTabs);
		const [isOptional, setIsOptional] = useState(getOption("IsOptional"));
		const [isPrinted, setIsPrinted] = useState(getOption("IsPrinted"));
		const [isTotalsIncluded, setIsTotalsIncluded] = useState(getOption("IsTotalsIncluded"));
		const [orderPorterRequired, setOrderPorterRequired] = useState(
			getOption("OrderPorterRequired")
		);
		const [listClasses, setListClasses] = useState("");
		const textInput = useRef(null);

		const darkMode = useDarkMode();

		const dropType = "sortableItem";
		const isStandardUser =
			!app.currentUser.IsContentMaintainer &&
			!app.currentUser.IsAdministrator &&
			!app.currentUser.IsStandardPlus;
		const isStandardUserPlus =
			!app.currentUser.IsContentMaintainer &&
			!app.currentUser.IsAdministrator &&
			app.currentUser.IsStandardPlus;
		const showShortMenu = isStandardUser && tabItem?.IsProtectedTab && !quosal.util.userCanModifyProtectedTab();
		const isStandardUserAndProtectedTab = isStandardUser && tabItem?.IsProtectedTab;

		const [{ isOver, canDrop }, drop] = useDrop(
			() => ({
				accept: dropType,
				drop: (item, monitor) => {
					const didDrop = monitor.didDrop();
					if (didDrop || item.id === id) {
						return;
					}
					onMove && onMove(item.id, id);
				},
				collect: (monitor) => ({
					isOver: monitor.isOver(),
					canDrop: monitor.canDrop()
				})
			}),
			[onMove],
			id
		);

		const isActive = isOver && canDrop;

		const [{ isDragging }, drag] = useDrag(
			() => ({
				type: dropType,
				item: { id, type: dropType },
				collect: (monitor) => ({
					isDragging: monitor.isDragging()
				}),
				canDrag: !isStandardUserAndProtectedTab && id !== ""
			}),
			[id, isStandardUserAndProtectedTab]
		);

		const handleClick = useCallback(() => {
			const openTabId = quote.Tabs.find((item) => item.IdQuoteTabs === id)?.IdQuoteTabs;
			const nextSelectedId = open == openTabId ? null : openTabId;
			updateOpenTab(nextSelectedId);
		}, [id, open, quote.Tabs, updateOpenTab]);

		const handleMenuClick = (event) => {
			setAnchorEl(event.currentTarget);
		};

		const handleMenuClose = () => {
			setAnchorEl(null);
		};

		const handleSheetMenuClick = (event) => {
			setSheetAnchorEl(event.currentTarget);
		};

		const handleSheetMenuClose = () => {
			setSheetAnchorEl(null);
		};

		const duplicateTab = useCallback(() => {
			handleDuplicateTab(false, id);
			setAnchorEl(null);
			setSheetAnchorEl(null);
		}, [handleDuplicateTab, id]);

		const duplicateTabAsRadioOption = useCallback(() => {
			handleDuplicateTab(true, id);
			setAnchorEl(null);
			setSheetAnchorEl(null);
		}, [handleDuplicateTab, id]);

		const optionGroupLabel =
			getOptionGroupLabelTitle() !== "" ? (
				<Chip
					label={getOptionGroupLabelTitle()}
					style={{ maxWidth: "150px", backgroundColor: getOptionGroupLabelColor() }}
				/>
			) : null;

		const tabGroupLabel =
			getTabGroupLabelTitle() !== "" ? (
				<Chip
					label={getTabGroupLabelTitle()}
					style={{ maxWidth: "150px", backgroundColor: getTabGroupLabelColor() }}
				/>
			) : null;

		const handleCheckBoxClick = useCallback(
			(optionName, event) => {
				let currTab = null;
				let newValue = null;
				quote.Tabs.forEach((tab) => {
					if (tab.IdQuoteTabs === tabItem?.IdQuoteTabs) {
						currTab = tab;
					}
				});

				if (event) {
					if (optionName === "IsOptional") {
						setIsOptional(false);
					}
					if (optionName === "IsPrinted") {
						setIsPrinted(false);
					}
					if (optionName === "IsTotalsIncluded") {
						setIsTotalsIncluded(false);
					}
					if (optionName === "OrderPorterRequired") {
						setOrderPorterRequired(false);
					}
					newValue = false;
				} else {
					if (optionName === "IsOptional") {
						setIsOptional(true);
					}
					if (optionName === "IsPrinted") {
						setIsPrinted(true);
					}
					if (optionName === "IsTotalsIncluded") {
						setIsTotalsIncluded(true);
					}
					if (optionName === "OrderPorterRequired") {
						setOrderPorterRequired(true);
					}
					newValue = true;
				}

				const updateFields = {};
				updateFields[optionName] = newValue;

				const updateApi = quosal.api.data.update(
					{
						fields: updateFields,
						queries: [
							{
								table: "QuoteTabs",
								where: [
									{
										field: "IdQuoteTabs",
										operator: "Equals",
										value: tabItem?.IdQuoteTabs
									}
								]
							}
						]
					},
					currTab.IdQuoteMain
				);
				updateApi.finished = function (msg) {
					quosal.sell.quote.updateFromApiResponse(msg);
				};
				updateApi.call();
			},
			[ids, tabItem?.IdQuoteTabs]
		);

		const handleSelectedTabIds = (tabIsDeselected) => {
			let copyOfTabs = [...selectedTabs];
			const tabNumber = getTabNumber(tabItem);
			if (tabIsDeselected) {
				const index = selectedTabs.findIndex((tab) => tab.tabId === tabItem?.IdQuoteTabs);
				if (index > -1) {
					copyOfTabs.splice(index, 1);
				}
			} else {
				copyOfTabs.push({ tabId: tabItem?.IdQuoteTabs, tabNumber: tabNumber });


			}
			setSelectedTabs(copyOfTabs.sort((a, b) => a.tabNumber - b.tabNumber));
		};

		
		const handleRenameTab = useCallback(() => {
			setRenameTab(true);
			updateOpenTab(tabItem?.IdQuoteTabs);
			setAnchorEl(null);
			setSheetAnchorEl(null);

			setTimeout(() => {
				textInput.current?.focus();
			}, 100);
		}, [tabItem?.IdQuoteTabs, updateOpenTab]);

		const handleKeyUp = useCallback(
			(event) => {
				if (event.keyCode === 27) {
					quote.Tabs.forEach((tab) => {
						if (tab.IdQuoteTabs === tabItem?.IdQuoteTabs) {
							setTabName(tab.TabName);
						}
					});

					setRenameTab(false);
					if (duplicatedTabId == id) {
						setDuplicatedTabId(null);
					}
				}
				if (event.keyCode === 13) {
					setRenameTab(false);
					if (tabItem?.TabName !== tabName) {
						saveTabName();
					}
					if (duplicatedTabId == id) {
						setDuplicatedTabId(null);
					}
				}
			},
			[duplicatedTabId, id, saveTabName, setDuplicatedTabId, tabItem?.IdQuoteTabs, tabItem?.TabName, tabName]
		);

		const handleOnBlur = useCallback(() => {
			setRenameTab(false);
			if (tabItem?.TabName !== tabName) {
				saveTabName();
			}
			if (duplicatedTabId == id) {
				setDuplicatedTabId(null);
			}
		}, [duplicatedTabId, id, saveTabName, setDuplicatedTabId, tabItem?.TabName, tabName]);

		const removeZeroQtyItems = (idTab = tabItem?.IdQuoteTabs) => {
			removeZeroQuantityItems(idTab, setChange, setLoading);
			setAnchorEl(null);
			setSheetAnchorEl(null);
		};

		const handleEditTab = () => {
			quote.Tabs.forEach((tab) => {
				if (tab.IdQuoteTabs === tabItem?.IdQuoteTabs) {
					app.currentModule.loadSubModule("tab.edit", {
						container: "quoteModule",
						query: `idquotetabs=${tab.IdQuoteTabs}`
					});
				}
			});
			sessionStorage.setItem("cpq_open_tab", tabItem?.IdQuoteTabs);
		};

		const getTabColor = () => {
			let tabColor = null;
			quote.Tabs.forEach((tab) => {
				if (tab.IdQuoteTabs === tabItem?.IdQuoteTabs) {
					tabColor = tab.TabColor;
				}
			});

			return tabColor === "gray" ? "black" : tabColor;
		};

		const updateSpreadsheet = useCallback(() => {
			let currTab = null;
			quote.Tabs.forEach((tab) => {
				if (tab.IdQuoteTabs === tabItem?.IdQuoteTabs) {
					currTab = tab;
				}
			});

			setIsSpreadsheetRefreshing(true);
			const updateApi = quosal.api.quote.updateFromSpreadsheets(currTab.IdQuoteMain);

			updateApi.finished = function (msg) {
				quosal.sell.quote.update(msg.quote);
				setIsSpreadsheetRefreshing(false);
				setChange();
				handleSheetMenuClose();
			};
			updateApi.call();
		}, [setChange, tabItem?.IdQuoteTabs]);

		const handleUpdateAllCosts = (tabId) => {
			quosal.navigation.navigate(
				quosal.util.url("updatecosts.quosalweb", `idquotetabs=${tabId}`, "tabonly=true")
			);
		};

		const handleSelectNewTab = useCallback(() => {
			const goToTab = function (nameOfTab) {
				let quoteNavigationModuleIndex = -1;
				let selectedTabId = null;
				if (app && app.currentModule && app.currentModule.Type === "QuoteDashboard") {
					quoteNavigationModuleIndex = quosal.util.findWithAttr(
						app.currentModule.subModules,
						"Type",
						"QuoteNavigation"
					);
				}

				if (nameOfTab) {
					const index = quosal.util.lastIndexOf(quote.Tabs, "TabName", nameOfTab);
					const tab = quote.Tabs[index];
					selectedTabId = tab.IdQuoteTabs;
				} else if (quoteNavigationModuleIndex > -1) {
					selectedTabId = quote.Tabs[quote.Tabs.length - 1].IdQuoteTabs;
				}
				updateOpenTab(selectedTabId);

				Dialog.close();
			};

			Dialog.open({
				height: "82%",
				width: "92%",
				title: "Choose Tab Template",
				message: <PickTabTemplate goToTab={goToTab} />,
				closeRequiresButton: true,
				links: [
					{
						title: "Finished",
						callback: goToTab
					}
				]
			});
		}, [updateOpenTab]);

		useEffect(() => {
			setListClasses(`prepareContentTab${darkMode ? " darkMode" : ""} BackgroundTabcolor ${getTabColor()}`);
		}, [darkMode]);

		useEffect(() => {
			if (id == duplicatedTabId) {
				setTimeout(() => {
					textInput.current?.focus();
				}, 100);
			}
		}, [duplicatedTabId, id]);

		useEffect(() => {
			if (duplicatedTabId == id) {
				const tab = quote.Tabs.find((t) => t.IdQuoteTabs === id);
				if (tab) {
					setTabName(tab.TabName || "");
				}
			}
		}, [duplicatedTabId, id, quote.Tabs]);

		useEffect(() => {
			quote.Tabs.forEach((tab) => {
				if (tab.IdQuoteTabs === tabItem?.IdQuoteTabs) {
					setGridRows(getRows(tab.IdQuoteTabs));
				}
			});
		}, [quote.Tabs]);

		useEffect(() => {
			if (id === "") setDisabledAddNewTab(true);
			else setDisabledAddNewTab(false);
		}, []);

		useEffect(() => {
			setTabName(getTabName(tabItem));
		}, [ getTabName, getTabNumber, renameTab ]);

	return {
		contentGrid,
		disabledAddNewTab,
		gridRows,
		anchorEl,
		sheetAnchorEl,
		renameTab,
		tabName,
		setTabName,
		getTabNumber,
		isSpreadsheetRefreshing,
		isOptional,
		isPrinted,
		isTotalsIncluded,
		isTabSelected,
		orderPorterRequired,
		isStandardUser,
		isStandardUserAndProtectedTab,
		isStandardUserPlus,
		showShortMenu,
		listClasses,
		textInput,
		darkMode,
		drop,
		isActive,
		isDragging,
		drag,
		handleClick,
		handleSelectedTabIds,
		handleMenuClick,
		handleMenuClose,
		handleSheetMenuClick,
		handleSheetMenuClose,
		duplicateTab,
		duplicateTabAsRadioOption,
		optionGroupLabel,
		tabGroupLabel,
		handleCheckBoxClick,
		handleRenameTab,
		handleKeyUp,
		handleOnBlur,
		removeZeroQtyItems,
		handleEditTab,
		getTabColor,
		updateSpreadsheet,
		handleUpdateAllCosts,
		handleSelectNewTab
	};
};

export default useDraggableExpandableContainer;
